
import {
  Vue, Component, Watch,
} from 'vue-property-decorator';
import { Lead as TLead } from '@/types';

@Component
export default class LeadEturnityDebug extends Vue {
  get lead(): TLead {
    return this.$store.getters.currentLead;
  }

  get showDebug() {
    // Mostra il debug solo per lead di tipo standard (Eturnity)
    return this.lead && this.lead.type === 'standard';
  }

  get hasEturnityData() {
    return this.configStatus || this.configStartedAt || this.configCompletedAt || this.hasConfigData || this.pdfReference || this.attachmentId || this.pdfReceivedAt;
  }

  // Helper method per selezionare la configurazione con priorità più alta
  private getSelectedEturnityConfig(): { configId: string; status: string } | null {
    const customValues = this.lead?.CustomValues || [];

    const configStatuses = customValues
      .filter((cv) => cv.name.startsWith('eturnityConfig_') && cv.name.endsWith('_status'));

    if (configStatuses.length > 0) {
      // Prima cerca le configurazioni attive (started, json_received)
      const activeConfigs = configStatuses.filter((cv) => ['started', 'json_received'].includes(cv.value || ''));

      // Se ci sono configurazioni attive, seleziona la più recente
      if (activeConfigs.length > 0) {
        const sortedByTime = activeConfigs.sort((a, b) => {
          const aTime = a.name.match(/eturnityConfig_\d+_(\d+)_status/)?.[1];
          const bTime = b.name.match(/eturnityConfig_\d+_(\d+)_status/)?.[1];
          return bTime && aTime ? parseInt(bTime, 10) - parseInt(aTime, 10) : 0;
        });

        const selectedConfig = sortedByTime[0];
        const configId = selectedConfig.name.replace('_status', '').replace('eturnityConfig_', '');
        return { configId, status: selectedConfig.value || 'Non impostato' };
      }

      // Se non ci sono configurazioni attive, fallback alle configurazioni completate
      const completedConfigs = configStatuses.filter((cv) => cv.value === 'completed');
      if (completedConfigs.length > 0) {
        const sortedByTime = completedConfigs.sort((a, b) => {
          const aTime = a.name.match(/eturnityConfig_\d+_(\d+)_status/)?.[1];
          const bTime = b.name.match(/eturnityConfig_\d+_(\d+)_status/)?.[1];
          return bTime && aTime ? parseInt(bTime, 10) - parseInt(aTime, 10) : 0;
        });

        const selectedConfig = sortedByTime[0];
        const configId = selectedConfig.name.replace('_status', '').replace('eturnityConfig_', '');
        return { configId, status: selectedConfig.value || 'Non impostato' };
      }

      // Fallback generico (non dovrebbe mai accadere)
      const selectedConfig = configStatuses[0];
      const configId = selectedConfig.name.replace('_status', '').replace('eturnityConfig_', '');
      return { configId, status: selectedConfig.value || 'Non impostato' };
    }

    return null;
  }

  get configStatus() {
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      // eslint-disable-next-line no-console
      console.log('LeadEturnityDebug: configStatus() - selected status:', selectedConfig.status, 'from config:', selectedConfig.configId);
      return selectedConfig.status;
    }

    // Nessuna configurazione Eturnity trovata
    // eslint-disable-next-line no-console
    console.log('LeadEturnityDebug: configStatus() - no config found');
    return 'Non impostato';
  }

  get configStartedAt() {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const startedValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_startedAt`);
      return startedValue?.dateValue || null;
    }

    return null;
  }

  get configCompletedAt() {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const completedValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_completedAt`);
      return completedValue?.dateValue || null;
    }

    return null;
  }

  get hasConfigData() {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const dataValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_data`);
      return dataValue?.value && dataValue.value.length > 0;
    }

    return false;
  }

  get configData() {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const dataValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_data`);
      return dataValue?.value || null;
    }

    return null;
  }

  get pdfReference() {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const referenceValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_pdfReference`);
      return referenceValue?.value || null;
    }

    return null;
  }

  get pdfReceivedAt() {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const receivedValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_pdfReceivedAt`);
      return receivedValue?.dateValue || null;
    }

    return null;
  }

  get attachmentId() {
    const customValues = this.lead?.CustomValues || [];
    const selectedConfig = this.getSelectedEturnityConfig();

    if (selectedConfig) {
      const attachmentIdValue = customValues.find((cv) => cv.name === `eturnityConfig_${selectedConfig.configId}_attachmentId`);
      return attachmentIdValue?.value || null;
    }

    return null;
  }

  get isMultipleConfig(): boolean {
    const customValues = this.lead?.CustomValues || [];
    const configStatuses = customValues.filter((cv) => cv.name.startsWith('eturnityConfig_') && cv.name.endsWith('_status'));
    return configStatuses.length > 1;
  }

  get selectedConfigId(): string | null {
    const selectedConfig = this.getSelectedEturnityConfig();
    return selectedConfig?.configId || null;
  }

  get hasPdfAttachment() {
    // Verifica se l'allegato specifico della configurazione Eturnity è presente
    if (!this.attachmentId) return false;

    const attachments = this.lead?.Attachments || [];
    const attachmentId = parseInt(this.attachmentId, 10);
    return attachments.some((att) => att.id === attachmentId);
  }

  get pdfAttachments() {
    // Mostra solo l'allegato specifico della configurazione Eturnity
    if (!this.attachmentId) return [];

    const attachments = this.lead?.Attachments || [];
    const attachmentId = parseInt(this.attachmentId, 10);
    const specificAttachment = attachments.find((att) => att.id === attachmentId);

    return specificAttachment ? [specificAttachment] : [];
  }

  get formattedConfigData() {
    if (!this.configData) return 'Nessun dato disponibile';

    try {
      const parsed = JSON.parse(this.configData);
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      return this.configData;
    }
  }

  get statusColor() {
    switch (this.configStatus) {
      case 'started':
        return 'orange';
      case 'json_received':
        return 'blue';
      case 'completed':
        return 'green';
      default:
        return 'grey';
    }
  }

  get statusText() {
    switch (this.configStatus) {
      case 'started':
        return 'Configurazione in corso';
      case 'json_received':
        return 'JSON ricevuto - In attesa PDF';
      case 'completed':
        return 'Configurazione completata';
      default:
        return 'Stato sconosciuto';
    }
  }

  get configStatusColor() {
    switch (this.configStatus) {
      case 'started':
        return 'orange';
      case 'json_received':
        return 'blue';
      case 'completed':
        return 'green';
      default:
        return 'grey';
    }
  }

  formatDate(date: string | Date | null) {
    if (!date) return 'Non impostata';

    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleString('it-IT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch (error) {
      return 'Data non valida';
    }
  }

  formatFileSize(bytes: number) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
  }

  getConfigCount() {
    const customValues = this.lead?.CustomValues || [];
    const configStatuses = customValues.filter((cv) => cv.name.startsWith('eturnityConfig_') && cv.name.endsWith('_status'));
    return configStatuses.length;
  }

  @Watch('lead')
  onLeadChange() {
    // eslint-disable-next-line no-console
    console.log('LeadEturnityDebug: lead changed, forcing reactivity update');

    // Forza la reattività del componente quando la lead viene aggiornata
    // Questo è necessario perché i computed properties potrebbero non rilevare
    // i cambiamenti nei custom values quando vengono aggiornati via WebSocket
    this.$forceUpdate();
  }

  @Watch('lead.CustomValues')
  onCustomValuesChange(newCustomValues: any[]) {
    // eslint-disable-next-line no-console
    console.log('LeadEturnityDebug: CustomValues changed, checking for Eturnity updates');

    // Verifica se ci sono stati cambiamenti nei custom values Eturnity (formato unificato)
    const eturnityCustomValues = newCustomValues?.filter((cv) => cv.name.startsWith('eturnityConfig_')) || [];

    if (eturnityCustomValues.length > 0) {
      // eslint-disable-next-line no-console
      console.log('LeadEturnityDebug: Eturnity custom values detected:', eturnityCustomValues.map((cv) => ({
        name: cv.name,
        value: cv.value,
        dateValue: cv.dateValue,
      })));

      // Forza l'aggiornamento del componente
      this.$forceUpdate();
    }
  }
}
